import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { MapPin, Calendar, Clock, Users, TrendingUp, Loader2, Car, AlertCircle, Navigation, Timer, Route, Map, Info, X, Sparkles } from 'lucide-react';
import { getCoordinates } from '../utils/cityCoordinates';
import { RideMap } from '../components/RideMap';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import MapPickerModal from '../components/MapPickerModal';
import { CostSharingSection } from '../components/ride/CostSharingSection';
import { RideModeToggle } from '../components/ride/RideModeToggle';
import { LuggageForm } from '../components/ride/LuggageForm';
import { VerificationRequired } from '../components/VerificationRequired';
import { PreferencesForm } from '../components/ride/PreferencesForm';
import { EventPickerModal } from '../components/EventPickerModal';
import './CreateRide.css';

import { BACKEND_URL, API } from '../utils/apiConfig';

export const CreateRide = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [startCoords, setStartCoords] = useState(null);
  const [endCoords, setEndCoords] = useState(null);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [vehicleLoading, setVehicleLoading] = useState(true);
  const [notesInfoOpen, setNotesInfoOpen] = useState(false);
  // Phase B — selected event POI as ride target (optional)
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventPickerOpen, setEventPickerOpen] = useState(false);

  const [formData, setFormData] = useState({
    start_city: '',
    start_street: '',
    start_number: '',
    destination_city: '',
    dest_street: '',
    dest_number: '',
    distance_km: '',
    intermediate_stops: '',
    departure_date: '',
    departure_time: '',
    seats_total: 3,
    luggage: {
      hand_luggage: true,
      suitcase_allowed: true,
      suitcase_max: 2,
      backpack: true,
      bicycle_allowed: false,
      ski_allowed: false
    },
    preferences: {
      smoking: user?.preferences?.smoking || 'not_allowed',
      pets: user?.preferences?.pets || 'none',
      music: user?.preferences?.music || 'normal',
      conversation: 'normal'
    },
    allow_pickup_on_route: true,
    max_detour_km: 5,
    time_window_minutes: 15,
    event_name: '',
    // Phase A1 — Flexibility fields
    time_flexible: false,
    wants_return_trip: false,
    return_time: '',
    return_date: '',
    date_tolerance_days: 0,
    notes: '',
    // Phase A2 — Corridor radius (1–20 km)
    corridor_radius_km: 5
  });

  // Event-Bridge: if user arrived from /events, prompt once for the event name
  const [eventPromptOpen, setEventPromptOpen] = useState(
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('source') === 'events'
  );

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rideMode, setRideMode] = useState('longdistance');
  const [mapPickerOpen, setMapPickerOpen] = useState(null); // 'start' | 'destination' | null
  const distance = parseFloat(formData.distance_km) || 0;

  // Auto-detect ride mode when distance changes
  useEffect(() => {
    if (distance > 0) {
      setRideMode(distance <= 50 ? 'city' : 'longdistance');
    }
  }, [distance]);

  // Fetch vehicle from profile on mount
  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.get(`${API}/users/my-vehicle`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVehicle(resp.data.vehicle);
      } catch (e) {
        console.error('Vehicle fetch failed:', e);
      } finally {
        setVehicleLoading(false);
      }
    };
    fetchVehicle();
  }, []);

  // Auto-calculate distance when both coords are set
  useEffect(() => {
    if (!startCoords || !endCoords) return;
    const fetchDistance = async () => {
      setDistanceLoading(true);
      try {
        const resp = await axios.get(`${API}/geo/distance`, {
          params: {
            from_lat: startCoords.lat, from_lng: startCoords.lng,
            to_lat: endCoords.lat, to_lng: endCoords.lng,
          }
        });
        const km = Math.round(resp.data.distance_km);
        setFormData(prev => ({ ...prev, distance_km: String(km) }));
        setRideMode(km <= 50 ? 'city' : 'longdistance');
      } catch (e) {
        console.error('Distance calc failed:', e);
      } finally {
        setDistanceLoading(false);
      }
    };
    fetchDistance();
  }, [startCoords, endCoords]);

  // Fallback: geocode city names if user types without selecting from dropdown
  const calcDistanceFromNames = async () => {
    if (!formData.start_city || !formData.destination_city) return;
    setDistanceLoading(true);
    try {
      const resp = await axios.post(`${API}/tracking/route`, {
        start_city: [formData.start_street, formData.start_number, formData.start_city].filter(Boolean).join(', '),
        destination_city: [formData.dest_street, formData.dest_number, formData.destination_city].filter(Boolean).join(', ')
      });
      const km = Math.round(resp.data.distance_km);
      setFormData(prev => ({ ...prev, distance_km: String(km) }));
      setRideMode(km <= 50 ? 'city' : 'longdistance');
      if (resp.data.start) setStartCoords({ lat: resp.data.start.lat, lng: resp.data.start.lng });
      if (resp.data.destination) setEndCoords({ lat: resp.data.destination.lat, lng: resp.data.destination.lng });
    } catch (e) {
      console.error('Route calc failed:', e);
    } finally {
      setDistanceLoading(false);
    }
  };

  const handleMapPickerSelect = (data) => {
    const city = data.city || '';
    const road = data.road || '';
    const house = data.house_number || '';
    const displayCity = road ? `${road} ${house}, ${city}`.trim() : city;
    if (mapPickerOpen === 'start') {
      setFormData(prev => ({ ...prev, start_city: displayCity || data.display_name, start_street: road, start_number: house }));
      setStartCoords({ lat: data.lat, lng: data.lng });
    } else if (mapPickerOpen === 'destination') {
      setFormData(prev => ({ ...prev, destination_city: displayCity || data.display_name, dest_street: road, dest_number: house }));
      setEndCoords({ lat: data.lat, lng: data.lng });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const stops = formData.intermediate_stops
        ? formData.intermediate_stops.split(',').map(s => s.trim()).filter(s => s)
        : [];
      const sCoords = startCoords || getCoordinates(formData.start_city);
      const eCoords = endCoords || getCoordinates(formData.destination_city);
      const token = localStorage.getItem('token');

      const startFull = [formData.start_street, formData.start_number, formData.start_city].filter(Boolean).join(', ');
      const destFull = [formData.dest_street, formData.dest_number, formData.destination_city].filter(Boolean).join(', ');

      const createResp = await axios.post(`${API}/rides/create`, {
        start_city: formData.start_city,
        destination_city: formData.destination_city,
        distance_km: parseFloat(formData.distance_km),
        intermediate_stops: stops,
        departure_date: formData.departure_date,
        departure_time: formData.departure_time,
        seats_total: formData.seats_total,
        ride_mode: rideMode,
        start_lat: sCoords.lat,
        start_lng: sCoords.lng,
        end_lat: eCoords.lat,
        end_lng: eCoords.lng,
        start_address: startFull || formData.start_city,
        end_address: destFull || formData.destination_city,
        luggage: formData.luggage,
        preferences: formData.preferences,
        allow_pickup_on_route: formData.allow_pickup_on_route,
        max_detour_km: formData.max_detour_km,
        time_window_minutes: formData.time_window_minutes,
        // Phase A1 — Flexibility fields
        time_flexible: !!formData.time_flexible,
        wants_return_trip: !!formData.wants_return_trip,
        return_time: formData.return_time || null,
        return_date: formData.return_date || null,
        date_tolerance_days: parseInt(formData.date_tolerance_days, 10) || 0,
        notes: (formData.notes || '').slice(0, 2000),
        corridor_radius_km: parseFloat(formData.corridor_radius_km) || 5.0,
        // Phase B — link to event POI when selected
        event_id: selectedEvent?.id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Phase B+ — surface auto-created return ride to driver
      const autoReturnId = createResp?.data?.return_ride_id || createResp?.data?.returnRideId;
      if (autoReturnId) {
        alert(t('createRide.returnCreatedToast', 'Rückfahrt automatisch erstellt — beide Fahrten sind nun aktiv.'));
      }

      // Show push confirmation before navigating
      const pushEnabled = localStorage.getItem('audioAlertsEnabled') !== 'false' || 
                          (typeof Notification !== 'undefined' && Notification.permission === 'granted');
      if (pushEnabled && !autoReturnId) {
        alert(t('createRide.rideCreatedConfirmation'));
      }
      navigate('/my-rides');
    } catch (err) {
      console.error('Create ride error:', err);
      setError(err.response?.data?.detail || t('createRide.createError'));
    } finally {
      setLoading(false);
    }
  };

  if (user?.role === 'Mitfahrer') {
    return (
      <div className="create-ride-container">
        <div className="error-card">
          <h2>{t('createRide.onlyForDrivers')}</h2>
          <p>{t('createRide.needDriverRole')}</p>
          <button onClick={() => navigate('/profile')} className="btn-primary">{t('createRide.editProfile')}</button>
        </div>
      </div>
    );
  }

  // Verification gate: block unverified users
  if (!user?.identity_verified) {
    return (
      <div className="create-ride-container">
        <VerificationRequired />
      </div>
    );
  }

  return (
    <div className="create-ride-container" data-testid="create-ride-page">
      <div className="create-ride-card">
        <div className="page-header">
          <h1>{t('createRide.title')}</h1>
          <p>{t('createRide.subtitle')}</p>
        </div>

        {/* Vehicle Info Card (auto-fetched) */}
        <div className="form-section" data-testid="vehicle-info-section">
          <h2><Car size={20} /> {t('createRide.sections.vehicle')}</h2>
          {vehicleLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '1rem', color: '#6b7280' }}>
              <Loader2 size={18} className="spinner" style={{ animation: 'spin 1s linear infinite' }} />
              {t('common.loading')}
            </div>
          ) : vehicle ? (
            <div data-testid="vehicle-display" style={{
              background: '#f8fafc', borderRadius: '10px', padding: '1rem',
              display: 'flex', alignItems: 'center', gap: '1rem',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '10px',
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
              }}>
                <Car size={24} style={{ color: 'white' }} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1f2937' }}>
                  {vehicle.make} {vehicle.model}
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                  {vehicle.color} {vehicle.license_plate && `· ${vehicle.license_plate}`}
                  {vehicle.has_ac && ` · ${t('createRide.vehicle.ac')}`}
                </div>
              </div>
            </div>
          ) : (
            <div data-testid="no-vehicle-cta" style={{
              background: '#fef3c7', borderRadius: '10px', padding: '1.25rem',
              border: '1px solid #fcd34d', textAlign: 'center'
            }}>
              <AlertCircle size={28} style={{ color: '#d97706', marginBottom: '0.5rem' }} />
              <p style={{ fontWeight: 600, color: '#92400e', marginBottom: '0.75rem' }}>
                {t('createRide.noVehicle.title')}
              </p>
              <p style={{ fontSize: '0.85rem', color: '#78350f', marginBottom: '1rem' }}>
                {t('createRide.noVehicle.description')}
              </p>
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="btn-primary"
                data-testid="add-vehicle-cta-btn"
              >
                {t('createRide.noVehicle.addVehicle')}
              </button>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="create-ride-form">
          {eventPromptOpen && (
            <div data-testid="event-name-banner" style={{
              padding: '14px 16px', marginBottom: 16, borderRadius: 12,
              background: 'linear-gradient(135deg, rgba(168,85,247,0.12), rgba(99,102,241,0.12))',
              border: '1px solid rgba(168,85,247,0.35)', color: '#4c1d95',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                ✨ {t('events.promptTitle', 'Zu welchem Event möchtest du?')}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  data-testid="event-name-input"
                  type="text"
                  value={formData.event_name}
                  placeholder={t('events.namePlaceholder', 'z. B. Coldplay Berlin, HSV vs. BVB, Oktoberfest…')}
                  onChange={e => setFormData(prev => ({ ...prev, event_name: e.target.value }))}
                  style={{ flex: 1, minWidth: 220, padding: '10px 12px', borderRadius: 10,
                           border: '1px solid rgba(168,85,247,0.5)', outline: 'none', fontSize: 14 }}
                />
                <button
                  type="button"
                  data-testid="event-name-save"
                  onClick={async () => {
                    const name = (formData.event_name || '').trim();
                    if (!name) return setEventPromptOpen(false);
                    try {
                      const token = localStorage.getItem('token');
                      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/suggested`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name }),
                      });
                    } catch {}
                    setEventPromptOpen(false);
                  }}
                  style={{ padding: '10px 14px', borderRadius: 10, background: '#6366f1', color: '#fff',
                           border: 'none', fontWeight: 700, cursor: 'pointer' }}
                >
                  {t('events.saveEvent', 'Übernehmen')}
                </button>
              </div>
            </div>
          )}

          <div className="form-section">
            <h2><MapPin size={20} /> {t('createRide.sections.rideDetails')}</h2>
            <div className="form-row" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <AddressAutocomplete
                  value={formData.start_city}
                  onChange={(val) => setFormData(prev => ({ ...prev, start_city: val }))}
                  onSelect={(s) => {
                    setStartCoords({ lat: s.lat, lng: s.lng });
                    if (s.road) setFormData(prev => ({ ...prev, start_street: s.road, start_number: s.house_number || '' }));
                  }}
                  placeholder={t('search.city') || 'Stadt'}
                  label={t('createRide.fields.from')}
                  required
                  testId="start-city-input"
                />
              </div>
              <button
                type="button"
                className="map-select-btn"
                data-testid="start-map-picker-btn"
                onClick={() => setMapPickerOpen('start')}
                title="Auf Karte wählen"
                style={{ marginTop: '1.6rem' }}
              >
                <Map size={18} />
              </button>
            </div>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label>{t('search.street') || 'Straße'}</label>
                <div className="input-wrapper">
                  <MapPin size={18} />
                  <input type="text" value={formData.start_street}
                    onChange={(e) => setFormData({ ...formData, start_street: e.target.value })}
                    placeholder={t('search.street') || 'Straße'}
                    data-testid="start-street-input" />
                </div>
              </div>
              <div className="form-group">
                <label>{t('search.houseNumber') || 'Nr.'}</label>
                <div className="input-wrapper">
                  <input type="text" value={formData.start_number}
                    onChange={(e) => setFormData({ ...formData, start_number: e.target.value })}
                    placeholder="Nr."
                    data-testid="start-number-input" />
                </div>
              </div>
            </div>

            <div className="form-row" style={{ marginTop: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <div style={{ flex: 1 }}>
                <AddressAutocomplete
                  value={formData.destination_city}
                  onChange={(val) => setFormData(prev => ({ ...prev, destination_city: val }))}
                  onSelect={(s) => {
                    setEndCoords({ lat: s.lat, lng: s.lng });
                    if (s.road) setFormData(prev => ({ ...prev, dest_street: s.road, dest_number: s.house_number || '' }));
                    setSelectedEvent(null);
                  }}
                  placeholder={t('search.city') || 'Stadt'}
                  label={t('createRide.fields.to')}
                  required
                  testId="dest-city-input"
                />
              </div>
              <button
                type="button"
                className="map-select-btn"
                data-testid="dest-map-picker-btn"
                onClick={() => setMapPickerOpen('destination')}
                title="Auf Karte wählen"
                style={{ marginTop: '1.6rem' }}
              >
                <Map size={18} />
              </button>
              <button
                type="button"
                data-testid="dest-event-picker-btn"
                onClick={() => setEventPickerOpen(true)}
                title={t('events.pickButton', 'Event als Ziel wählen')}
                style={{
                  marginTop: '1.6rem',
                  background: selectedEvent
                    ? 'linear-gradient(135deg,#7c3aed 0%,#ec4899 100%)'
                    : '#fff',
                  color: selectedEvent ? '#fff' : '#7c3aed',
                  border: `1.5px solid ${selectedEvent ? '#7c3aed' : '#c4b5fd'}`,
                  borderRadius: 8,
                  padding: '0.55rem 0.75rem',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 13, fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                🎯 {t('events.pickButton', 'Event')}
              </button>
            </div>

            {selectedEvent && (
              <div
                data-testid="selected-event-pill"
                style={{
                  marginTop: '0.5rem', padding: '0.55rem 0.8rem',
                  borderRadius: 10,
                  background: 'linear-gradient(135deg,rgba(124,58,237,0.08) 0%,rgba(236,72,153,0.08) 100%)',
                  border: '1px solid rgba(124,58,237,0.25)',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13, color: '#0f172a',
                }}
              >
                <span style={{ fontSize: 16 }}>🎯</span>
                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <strong>{selectedEvent.name}</strong>
                  {selectedEvent.city && <span style={{ color: '#64748b' }}> · {selectedEvent.city}</span>}
                </span>
                <button
                  type="button"
                  data-testid="selected-event-clear"
                  onClick={() => setSelectedEvent(null)}
                  aria-label="clear event"
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: '#7c3aed', display: 'flex', alignItems: 'center', padding: 2,
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            )}
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
              <div className="form-group">
                <label>{t('search.street') || 'Straße'}</label>
                <div className="input-wrapper">
                  <MapPin size={18} />
                  <input type="text" value={formData.dest_street}
                    onChange={(e) => setFormData({ ...formData, dest_street: e.target.value })}
                    placeholder={t('search.street') || 'Straße'}
                    data-testid="dest-street-input" />
                </div>
              </div>
              <div className="form-group">
                <label>{t('search.houseNumber') || 'Nr.'}</label>
                <div className="input-wrapper">
                  <input type="text" value={formData.dest_number}
                    onChange={(e) => setFormData({ ...formData, dest_number: e.target.value })}
                    placeholder="Nr."
                    data-testid="dest-number-input" />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>{t('createRide.fields.distance')} * {distanceLoading && <Loader2 size={14} className="spinner" style={{ display: 'inline', animation: 'spin 1s linear infinite' }} />}</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div className="input-wrapper" style={{ flex: 1 }}>
                  <TrendingUp size={18} />
                  <input
                    type="number" value={formData.distance_km}
                    onChange={(e) => {
                      const km = parseInt(e.target.value) || 0;
                      setFormData({ ...formData, distance_km: e.target.value });
                      setRideMode(km <= 50 ? 'city' : 'longdistance');
                    }}
                    placeholder={t('createRide.fields.distancePlaceholder')}
                    min="1" step="1" required
                    data-testid="distance-km-input"
                  />
                </div>
                <button
                  type="button"
                  data-testid="calc-distance-btn"
                  onClick={calcDistanceFromNames}
                  disabled={distanceLoading || !formData.start_city || !formData.destination_city}
                  style={{
                    padding: '0.65rem 1rem', background: '#2563eb', color: 'white', border: 'none',
                    borderRadius: '10px', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
                    whiteSpace: 'nowrap', opacity: distanceLoading ? 0.5 : 1
                  }}
                >
                  {distanceLoading ? '...' : 'KM berechnen'}
                </button>
              </div>
              <small style={{ display: 'block', marginTop: '0.3rem', color: '#6b7280' }}>
                {distance > 0
                  ? `${distance} km — ${rideMode === 'city' ? 'Stadtfahrt (≤50km)' : 'Langstrecke (>50km)'}`
                  : t('createRide.fields.distanceHint')
                }
              </small>
            </div>
            <div className="form-group">
              <label>{t('createRide.fields.intermediateStops')} ({t('common.optional')})</label>
              <div className="input-wrapper">
                <MapPin size={18} />
                <input type="text" value={formData.intermediate_stops} onChange={(e) => setFormData({ ...formData, intermediate_stops: e.target.value })} placeholder={t('createRide.fields.stopsPlaceholder')} />
              </div>
              <small>{t('createRide.fields.stopsHint')}</small>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('createRide.fields.date')} *</label>
                <div className="input-wrapper">
                  <Calendar size={18} />
                  <input type="date" data-testid="ride-date-input" value={formData.departure_date} onChange={(e) => setFormData({ ...formData, departure_date: e.target.value })} min={new Date().toISOString().split('T')[0]} required />
                </div>
              </div>
              <div className="form-group">
                <label>{t('createRide.fields.time')} *</label>
                <div className="input-wrapper">
                  <Clock size={18} />
                  <input type="time" data-testid="ride-time-input" value={formData.departure_time} onChange={(e) => setFormData({ ...formData, departure_time: e.target.value })} disabled={formData.time_flexible} required={!formData.time_flexible} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    data-testid="ride-time-flexible"
                    checked={formData.time_flexible}
                    onChange={(e) => setFormData({ ...formData, time_flexible: e.target.checked })}
                  />
                  {t('createRide.fields.timeFlexible', 'Uhrzeit egal')}
                </label>
              </div>
            </div>

            {/* Phase A1 — Datums-Toleranz */}
            <div className="form-group" data-testid="ride-date-tolerance-group">
              <label>{t('createRide.fields.dateTolerance', 'Datums-Toleranz')}</label>
              <select
                data-testid="ride-date-tolerance"
                value={formData.date_tolerance_days}
                onChange={(e) => setFormData({ ...formData, date_tolerance_days: parseInt(e.target.value, 10) })}
                style={{
                  width: '100%', padding: '0.65rem 0.75rem', borderRadius: 8,
                  border: '1px solid #e2e8f0', fontSize: 14, background: '#fff'
                }}
              >
                <option value={0}>{t('createRide.fields.dateToleranceExact', 'Exakt am gewählten Datum')}</option>
                <option value={1}>{t('createRide.fields.dateTolerance1', '±1 Tag')}</option>
                <option value={2}>{t('createRide.fields.dateTolerance2', '±2 Tage')}</option>
                <option value={3}>{t('createRide.fields.dateTolerance3', '±3 Tage')}</option>
                <option value={4}>{t('createRide.fields.dateTolerance4', '±4 Tage')}</option>
                <option value={5}>{t('createRide.fields.dateTolerance5', '±5 Tage')}</option>
              </select>
              <small style={{ color: '#64748b', fontSize: 12 }}>
                {t('createRide.fields.dateToleranceHint', 'Mitfahrer mit ±N Tage Toleranz werden ebenfalls gematcht.')}
              </small>
            </div>

            {/* Phase A1 — Rückfahrt */}
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0' }} data-testid="ride-return-trip-group">
              <div>
                <label style={{ fontWeight: 600, marginBottom: '0.15rem', display: 'block' }}>
                  {t('createRide.fields.wantsReturnTrip', 'Mit Rückfahrt wenn möglich')}
                </label>
                <small style={{ color: '#6b7280' }}>
                  {t('createRide.fields.wantsReturnTripHint', 'Wir matchen dich auch mit passenden Rückfahrten.')}
                </small>
              </div>
              <button
                type="button"
                data-testid="ride-wants-return-toggle"
                onClick={() => setFormData(prev => ({ ...prev, wants_return_trip: !prev.wants_return_trip }))}
                style={{
                  width: '52px', height: '28px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                  background: formData.wants_return_trip ? '#2563eb' : '#d1d5db',
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0
                }}
              >
                <span style={{
                  position: 'absolute', top: '2px',
                  left: formData.wants_return_trip ? '26px' : '2px',
                  width: '24px', height: '24px', borderRadius: '12px',
                  background: 'white', transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </button>
            </div>
            {formData.wants_return_trip && (
              <div className="form-group" data-testid="ride-return-time-group">
                <label>{t('createRide.fields.returnDate', 'Rückfahrt-Datum & Zeit (optional)')}</label>
                <small style={{ color: '#6b7280', display: 'block', marginBottom: 6 }}>
                  {t('createRide.fields.returnDateHint', 'Leer lassen → gleicher Tag wie Hinfahrt.')}
                </small>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <div className="input-wrapper" style={{ flex: '1 1 160px', minWidth: 0 }}>
                    <Calendar size={18} />
                    <input
                      type="date"
                      data-testid="ride-return-date-input"
                      value={formData.return_date || ''}
                      min={formData.departure_date || undefined}
                      onChange={(e) => setFormData({ ...formData, return_date: e.target.value })}
                    />
                  </div>
                  <div className="input-wrapper" style={{ flex: '1 1 120px', minWidth: 0 }}>
                    <Clock size={18} />
                    <input
                      type="time"
                      data-testid="ride-return-time-input"
                      value={formData.return_time}
                      onChange={(e) => setFormData({ ...formData, return_time: e.target.value })}
                    />
                  </div>
                </div>
                {/* Sprint 5 — commuter pattern bumper (≥4 days gap) */}
                {(() => {
                  if (!formData.departure_date || !formData.return_date) return null;
                  const d1 = new Date(formData.departure_date);
                  const d2 = new Date(formData.return_date);
                  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return null;
                  const days = Math.round((d2 - d1) / 86400000);
                  if (days < 4) return null;
                  return (
                    <div
                      data-testid="commuter-hint"
                      style={{
                        marginTop: 8,
                        padding: '8px 12px',
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(236,72,153,0.06))',
                        border: '1px solid rgba(124,58,237,0.18)',
                        fontSize: 12,
                        color: '#6d28d9',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontWeight: 600,
                      }}
                    >
                      <Sparkles size={13} />
                      {t('bundle.commuterHint', { days })}
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="form-group">
              <label>{t('createRide.fields.seats')} *</label>
              <div className="input-wrapper">
                <Users size={18} />
                <input type="number" value={formData.seats_total} onChange={(e) => setFormData({ ...formData, seats_total: parseInt(e.target.value) || 1 })} min="1" max="8" required />
              </div>
            </div>

            {/* Phase A1 — Weitere Anmerkungen mit Info-Popup */}
            <div className="form-group" data-testid="ride-notes-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {t('createRide.fields.notesLabel', 'Weitere Anmerkungen')}
                <button
                  type="button"
                  data-testid="ride-notes-info-btn"
                  onClick={() => setNotesInfoOpen(true)}
                  aria-label={t('common.info', 'Info')}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    color: '#2563eb', padding: 0, display: 'inline-flex', alignItems: 'center'
                  }}
                >
                  <Info size={16} />
                </button>
              </label>
              <textarea
                data-testid="ride-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value.slice(0, 2000) })}
                rows={3}
                maxLength={2000}
                placeholder={t('createRide.fields.notesPlaceholder', 'Treffpunkt, Hinweise zum Gepäck, Sonderwünsche …')}
                style={{
                  width: '100%', padding: '0.65rem 0.75rem', borderRadius: 8,
                  border: '1px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit',
                  resize: 'vertical', minHeight: 70
                }}
              />
              <small style={{ color: '#64748b', fontSize: 12 }}>
                {formData.notes.length}/2000
              </small>
            </div>

            {/* Phase A2 — Korridor-Radius Slider */}
            <div className="form-group" data-testid="ride-corridor-group" style={{ marginTop: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Route size={16} />
                  {t('createRide.fields.corridorRadius', 'Korridor-Umkreis')}
                </span>
                <span style={{
                  fontSize: 13, fontWeight: 700, color: '#2563eb',
                  padding: '2px 10px', borderRadius: 999, background: 'rgba(37,99,235,0.08)'
                }} data-testid="ride-corridor-value">
                  {formData.corridor_radius_km} km
                </span>
              </label>
              <input
                type="range"
                data-testid="ride-corridor-slider"
                min="1" max="20" step="1"
                value={formData.corridor_radius_km}
                onChange={(e) => setFormData({ ...formData, corridor_radius_km: parseInt(e.target.value, 10) })}
                style={{ width: '100%', marginTop: 8 }}
              />
              <small style={{ color: '#64748b', fontSize: 12 }}>
                {t('createRide.fields.corridorRadiusHint',
                  'Wir matchen Mitfahrer, deren Start/Ziel innerhalb dieses Umkreises um deine Route liegt.')}
              </small>
            </div>
          </div>

          {/* ── Matching Parameters ── */}
          <div className="form-section" data-testid="matching-params-section">
            <h2><Route size={20} /> Matching-Parameter</h2>

            {/* Pickup on Route Toggle */}
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0' }}>
              <div>
                <label style={{ fontWeight: 600, marginBottom: '0.15rem', display: 'block' }}>Zustieg auf der Strecke erlauben</label>
                <small style={{ color: '#6b7280' }}>Mitfahrer können entlang deiner Route zusteigen</small>
              </div>
              <button
                type="button"
                data-testid="pickup-on-route-toggle"
                onClick={() => setFormData(prev => ({ ...prev, allow_pickup_on_route: !prev.allow_pickup_on_route }))}
                style={{
                  width: '52px', height: '28px', borderRadius: '14px', border: 'none', cursor: 'pointer',
                  background: formData.allow_pickup_on_route ? '#2563eb' : '#d1d5db',
                  position: 'relative', transition: 'background 0.2s', flexShrink: 0
                }}
              >
                <span style={{
                  position: 'absolute', top: '2px',
                  left: formData.allow_pickup_on_route ? '26px' : '2px',
                  width: '24px', height: '24px', borderRadius: '12px',
                  background: 'white', transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }} />
              </button>
            </div>

            {/* Max Detour */}
            {formData.allow_pickup_on_route && (
              <div className="form-group" data-testid="max-detour-group">
                <label><Navigation size={16} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} /> Max. Umweg</label>
                <div className="input-wrapper">
                  <Navigation size={18} />
                  <input
                    type="number"
                    value={formData.max_detour_km}
                    onChange={(e) => setFormData({ ...formData, max_detour_km: Math.max(0, parseFloat(e.target.value) || 0) })}
                    min="0" max="50" step="0.5"
                    data-testid="max-detour-input"
                  />
                </div>
                <small style={{ color: '#6b7280' }}>
                  Wie weit bist du bereit, von deiner Route abzuweichen? ({formData.max_detour_km} km)
                </small>
              </div>
            )}

            {/* Time Window */}
            <div className="form-group" data-testid="time-window-group">
              <label><Timer size={16} style={{ marginRight: '0.3rem', verticalAlign: 'middle' }} /> Zeitfenster</label>
              <div className="input-wrapper">
                <Timer size={18} />
                <input
                  type="number"
                  value={formData.time_window_minutes}
                  onChange={(e) => setFormData({ ...formData, time_window_minutes: Math.max(0, parseInt(e.target.value) || 0) })}
                  min="0" max="120" step="5"
                  data-testid="time-window-input"
                />
              </div>
              <small style={{ color: '#6b7280' }}>
                Abfahrt zwischen {formData.time_window_minutes} Minuten vor und nach der geplanten Zeit
              </small>
            </div>
          </div>

          {/* Route Map Preview */}
          {formData.start_city && formData.destination_city && (
            <div className="form-section" style={{ marginTop: '1rem' }}>
              <h2><MapPin size={20} /> Routenvorschau</h2>
              <div data-testid="create-ride-map" style={{ height: '400px', borderRadius: '12px', overflow: 'hidden', border: '2px solid #e2e8f0' }}>
                <RideMap
                  startCity={[formData.start_street, formData.start_number, formData.start_city].filter(Boolean).join(', ')}
                  destinationCity={[formData.dest_street, formData.dest_number, formData.destination_city].filter(Boolean).join(', ')}
                  startCoords={startCoords}
                  endCoords={endCoords}
                  showETA={true}
                />
              </div>
            </div>
          )}

          <CostSharingSection distance={distance} seats={formData.seats_total} rideMode={rideMode} />
          <LuggageForm formData={formData} setFormData={setFormData} />
          <PreferencesForm formData={formData} setFormData={setFormData} />

          {error && <div className="error-box">{error}</div>}

          <div className="form-actions">
            <button type="button" onClick={() => navigate('/my-rides')} className="btn-secondary">{t('common.cancel')}</button>
            <button type="submit" className="btn-primary" disabled={loading || distance === 0} data-testid="create-ride-submit-btn">
              {loading ? t('createRide.submitting') : t('createRide.submit')}
            </button>
          </div>
        </form>
      </div>

      <MapPickerModal
        open={mapPickerOpen !== null}
        onClose={() => setMapPickerOpen(null)}
        onSelect={handleMapPickerSelect}
        label={mapPickerOpen === 'start' ? (t('createRide.fields.from') + ' auf Karte wählen') : (t('createRide.fields.to') + ' auf Karte wählen')}
      />

      <EventPickerModal
        open={eventPickerOpen}
        onClose={() => setEventPickerOpen(false)}
        onSelect={(ev) => {
          setSelectedEvent(ev);
          setEndCoords({ lat: ev.lat, lng: ev.lng });
          setFormData(prev => ({
            ...prev,
            destination_city: ev.city || ev.venue_name || ev.name,
            dest_street: ev.venue_name || '',
            dest_number: '',
          }));
          setEventPickerOpen(false);
        }}
      />

      {notesInfoOpen && (
        <div
          data-testid="ride-notes-info-modal"
          onClick={() => setNotesInfoOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.55)',
            backdropFilter: 'blur(6px)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 460, width: '100%', background: '#fff', borderRadius: 14,
              padding: '20px 22px 18px', boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
              position: 'relative'
            }}
          >
            <button
              data-testid="ride-notes-info-close"
              onClick={() => setNotesInfoOpen(false)}
              style={{
                position: 'absolute', top: 10, right: 10,
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: '#64748b', padding: 6, borderRadius: 6
              }}
              aria-label={t('common.close', 'Schließen')}
            >
              <X size={18} />
            </button>
            <h3 style={{ margin: '0 0 10px', color: '#0f172a', fontSize: 16, fontWeight: 700 }}>
              {t('createRide.notesInfo.title', 'Information')}
            </h3>
            <p style={{ margin: 0, color: '#334155', fontSize: 14, lineHeight: 1.55 }}>
              {t('createRide.notesInfo.body',
                'Hier ist Platz für den genauen Treffpunkt, Informationen die dir besonders wichtig sind oder solche, die sonst nirgends einen Platz in deinem Eintrag haben. ACHTUNG: Gib hier keine Kontaktdaten an, denn Eintrag und Bemerkungen sind für jeden sichtbar.'
              )}
            </p>
            <button
              data-testid="ride-notes-info-ok"
              onClick={() => setNotesInfoOpen(false)}
              style={{
                marginTop: 16, width: '100%', padding: '0.7rem 1rem',
                background: '#2563eb', color: '#fff', border: 'none',
                borderRadius: 8, fontWeight: 600, cursor: 'pointer'
              }}
            >
              {t('common.close', 'Schließen')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
